/** Les trois hooks de section, construits sur la même fabrique. */

import {
  fetchAdsStats,
  fetchAmazonAdsStats,
  fetchGoogleAdsStats,
  fetchAmazonStats,
  fetchAnalysis,
  fetchCatalogStats,
  fetchEngagementStats,
  fetchLearningStats,
  fetchPeriodStats,
  fetchPersonalizationStats,
  fetchSalesStats,
  fetchSubscriptionSalesStats,
  fetchSessionStats,
  fetchSubscriptionStats,
} from '../lib/api';
import type {
  AdsStats,
  AmazonAdsStats,
  AmazonStats,
  AnalysisEnvelope,
  CatalogStats,
  EngagementStats,
  LearningStats,
  PeriodStats,
  PersonalizationStats,
  RefundBasis,
  SalesStats,
  SubscriptionSalesStats,
  SessionStats,
  SubscriptionStats,
} from '../types';
import { useSectionStats, type SectionStatsOptions } from './useSectionStats';

export function usePeriodStats(options: SectionStatsOptions) {
  return useSectionStats<PeriodStats>('period-stats', fetchPeriodStats, {
    topTracksLimit: 20,
    ...options,
  });
}

export function useLearningStats(options: SectionStatsOptions) {
  return useSectionStats<LearningStats>('learning-stats', fetchLearningStats, options);
}

export function usePersonalizationStats(options: SectionStatsOptions) {
  return useSectionStats<PersonalizationStats>(
    'personalization-stats',
    fetchPersonalizationStats,
    options,
  );
}

export function useSessionStats(options: SectionStatsOptions) {
  return useSectionStats<SessionStats>('session-stats', fetchSessionStats, options);
}

export function useCatalogStats(options: SectionStatsOptions) {
  return useSectionStats<CatalogStats>('catalog-stats', fetchCatalogStats, options);
}

export function useEngagementStats(options: SectionStatsOptions) {
  return useSectionStats<EngagementStats>('engagement-stats', fetchEngagementStats, options);
}

/**
 * Une analyse explicative. La clé de cache inclut le nom, sinon les cinq
 * analyses se partageraient la même entrée.
 */
export function useAnalysis<T>(name: string, options: SectionStatsOptions) {
  return useSectionStats<AnalysisEnvelope<T>>(
    `analysis-${name}`,
    (query, token, signal) => fetchAnalysis<T>(name, query, token, signal),
    options,
  );
}

export function useSubscriptionStats(options: SectionStatsOptions) {
  return useSectionStats<SubscriptionStats>('subscription-stats', fetchSubscriptionStats, options);
}

/**
 * Ventes Shopify. Le rattachement des remboursements entre dans la clé de
 * cache : les deux lectures ne partagent pas une entrée.
 */
export function useSalesStats(options: SectionStatsOptions & { refundBasis: RefundBasis }) {
  const { refundBasis, ...rest } = options;
  return useSectionStats<SalesStats>(
    `sales-${refundBasis}`,
    (query, token, signal) => fetchSalesStats({ from: query.from, to: query.to, refundBasis }, token, signal),
    rest,
  );
}

/** Abonnements Stripe de la section Ventes. */
export function useSubscriptionSales(options: SectionStatsOptions) {
  return useSectionStats<SubscriptionSalesStats>('sales-subscriptions', fetchSubscriptionSalesStats, options);
}

/** Publicité Meta de la section Ventes. */
export function useAdsStats(options: SectionStatsOptions) {
  return useSectionStats<AdsStats>('sales-ads', fetchAdsStats, options);
}

/** Ventes Amazon. */
export function useAmazonStats(options: SectionStatsOptions) {
  return useSectionStats<AmazonStats>('sales-amazon', fetchAmazonStats, options);
}

/** Publicité Amazon. */
export function useAmazonAdsStats(options: SectionStatsOptions) {
  return useSectionStats<AmazonAdsStats>('sales-amazon-ads', fetchAmazonAdsStats, options);
}

/** Google Ads — même réponse qu'Amazon Ads. */
export function useGoogleAdsStats(options: SectionStatsOptions) {
  return useSectionStats<AmazonAdsStats>('sales-google-ads', fetchGoogleAdsStats, options);
}
